import { MB_Modal } from '@mightybyte/rnw.components.modal';
import React, { useCallback, useState } from 'react';
import { getColors } from '../../../../../constants/colors';
import { NotificationTemplateForAdmin } from '@sapphicsavvy/business';
import { StyleSheet } from 'react-native';
import { trpc } from '../../../../../apiCalls/trpcClient';
import { ShadowTextInput } from '../../../../helperComponents/ShadowTextInput';
import { formUtils, useForm } from '../../../../../hooks/useForm';
import { z } from 'zod';
import { View, Text } from 'react-native';
import { quarterSharedStyles } from '../quarter/quarterSharedStyles';
import { MB_Button } from '@mightybyte/rnw.components.button';
import { mbTextStyles } from '@mightybyte/rnw.utils.style-utils';
import { textStyles } from '../../../../../constants/textStyles';
import { utils } from '../../../../../utils/utils';

const COLORS = getColors();

type ValidForm = {
    phoneNumber: string;
};

const schemas = {
    phoneNumber: z
        .string()
        .min(1, { message: 'Phone number is required' })
        .regex(formUtils.phoneRegex, 'Invalid phone number'),
};

interface Props {
    template: NotificationTemplateForAdmin | null;
    setTemplate: (bool: NotificationTemplateForAdmin | null) => void;
}

export function TestModal({ template, setTemplate }: Props) {
    const { mutate: testNotification } =
        trpc.admin.notification.testNotification.useMutation();
    const { setPhoneNumber, phoneNumber, errors } = useForm<Partial<ValidForm>>(
        { schemas },
        { phoneNumber: '' },
    );

    const [error, setError] = useState('');

    const resetState = useCallback(() => {
        setError('');
        setPhoneNumber('');
    }, []);

    const closeModal = useCallback(() => {
        resetState();
        setTemplate(null);
    }, []);

    const send = useCallback(() => {
        if (!template) return;
        if (!phoneNumber?.trim()) {
            setError('Phone number is required.');
            return;
        }

        const { customMessage, customTitle, type } = template;

        testNotification(
            {
                customMessage: customMessage || undefined,
                customTitle: customTitle || undefined,
                type,
                phoneNumber: phoneNumber || '',
            },
            {
                onSuccess: () => {
                    closeModal();

                    setTimeout(() => {
                        utils.showToast('success', 'Test notification sent!');
                    }, 500);
                },
                onError: (err) => {
                    setError(err.message);
                },
            },
        );
    }, [template, phoneNumber, utils, closeModal]);

    const COLORS = getColors();

    return (
        <MB_Modal
            isVisible={!!template}
            onDismiss={() => setTemplate(null)}
            scrollViewContentContainerStyle={{ padding: 0 }}
            childrenWrapperStyle={{ padding: 0, width: 400 }}
            hideCloseButton
        >
            <View style={styles.header}>
                <Text style={styles.headerText}>Test Notification</Text>
            </View>
            <View style={styles.body}>
                <ShadowTextInput
                    title="Phone Number"
                    placeholder="+1 000 000 0000"
                    showRequiredAsteriks
                    onChangeText={(text) => {
                        setPhoneNumber(text), setError('');
                    }}
                    value={phoneNumber}
                    keyboardType="phone-pad"
                    isError={errors.phoneNumber !== undefined || !!error}
                    errorMessage={errors.phoneNumber || error}
                    returnKeyType="done"
                    style={{ width: '100%' }}
                    containerStyle={{ width: '100%' }}
                />
            </View>

            <View style={styles.buttonRow}>
                <MB_Button
                    title={'Send'}
                    onPress={send}
                    style={[
                        quarterSharedStyles.button,
                        { alignSelf: 'center', marginRight: 15, width: 150 },
                    ]}
                    textStyle={quarterSharedStyles.buttonText}
                />

                <MB_Button
                    title={'Cancel'}
                    onPress={closeModal}
                    style={[
                        quarterSharedStyles.secondaryBtn,
                        { width: 150, marginLeft: 15 },
                    ]}
                    textStyle={quarterSharedStyles.secondaryBtnText}
                />
            </View>
        </MB_Modal>
    );
}

const styles = StyleSheet.create({
    modalContainer: {
        padding: 0,

        width: 400,
    },

    body: {
        paddingHorizontal: 20,
        paddingVertical: 50,
        alignItems: 'center',
        width: '100%',
    },

    buttonRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F5F5F5',
        padding: 20,
        width: '100%',
    },

    headerText: mbTextStyles([
        textStyles.largerText,
        {
            color: COLORS.orange,
            fontSize: 20,
            paddingBottom: 8,
        },
    ]),

    header: {
        backgroundColor: '#FEF2EE',
        width: '100%',
        padding: 20,
    },
});
